import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { ProjectItem, Vendor } from "./types";
import { statusOptions } from "./types";
import StagesCard from "./StagesCard";
import VendorPartnerSection from "./VendorPartnerSection";
import MarkdownField from "./MarkdownField";
import { useAuth } from "../../lib/auth";

interface DetailsTabProps {
  project: ProjectItem;
  vendors: Vendor[];
  onUpdate: (updates: Partial<ProjectItem>) => Promise<void>;
  onUpdateWithConfirm: (updates: Partial<ProjectItem>) => Promise<void> | void;
}

export default function DetailsTab({
  project,
  vendors,
  onUpdate,
  onUpdateWithConfirm,
}: DetailsTabProps) {
  const { canWrite } = useAuth();
  const ro = !canWrite;
  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Project Information">
        <Form layout="vertical" disabled={ro}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Start Date">
                <DatePicker
                  aria-label="Project start date"
                  value={project.startDate ? dayjs(project.startDate) : null}
                  onChange={(date) =>
                    onUpdate({
                      startDate: date ? date.format("YYYY-MM-DD") : "",
                    })
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="End Date">
                <DatePicker
                  aria-label="Project end date"
                  value={project.endDate ? dayjs(project.endDate) : null}
                  onChange={(date) =>
                    onUpdate({
                      endDate: date ? date.format("YYYY-MM-DD") : "",
                    })
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <VendorPartnerSection
                project={project}
                vendors={vendors}
                onChange={onUpdate}
              />
            </Col>
            <Col span={12}>
              <Form.Item label="Status">
                <Select
                  aria-label="Project status"
                  value={project.status}
                  onChange={(value) => onUpdateWithConfirm({ status: value })}
                  options={statusOptions.map((group) => ({
                    label: group.group,
                    options: group.options.map((option) => ({
                      value: option,
                      label: option,
                    })),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <StagesCard projectId={project.id} />

      <Card title="Project Description">
        <MarkdownField
          ariaLabel="Project description"
          value={project.projectDescription || ""}
          onChange={(markdown) => onUpdate({ projectDescription: markdown })}
          placeholder="Provide a detailed project description..."
          disabled={ro}
          minHeight={120}
        />
      </Card>

      <Card title="Problem & Impact">
        <Form layout="vertical" disabled={ro}>
          <Form.Item label="Problem Being Solved">
            <MarkdownField
              ariaLabel="Problem being solved"
              value={project.problemBeingSolved || ""}
              onChange={(markdown) => onUpdate({ problemBeingSolved: markdown })}
              placeholder="What problem is this project addressing?"
              disabled={ro}
            />
          </Form.Item>
          <Form.Item label="Current Problem Impact">
            <MarkdownField
              ariaLabel="Current problem impact"
              value={project.currentProblemImpact || ""}
              onChange={(markdown) => onUpdate({ currentProblemImpact: markdown })}
              placeholder="What is the current impact of this problem?"
              disabled={ro}
            />
          </Form.Item>
          <Form.Item label="Impact/Reach">
            <MarkdownField
              ariaLabel="Impact and reach"
              value={project.impactReach || ""}
              onChange={(markdown) => onUpdate({ impactReach: markdown })}
              placeholder="What is the expected impact and reach?"
              disabled={ro}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card title="Success Metrics">
        <Form layout="vertical" disabled={ro}>
          <Form.Item label="What would indicate success?">
            <MarkdownField
              ariaLabel="Success indicators"
              value={project.successIndicators || ""}
              onChange={(markdown) => onUpdate({ successIndicators: markdown })}
              placeholder="What are the key indicators of success?"
              disabled={ro}
            />
          </Form.Item>
          <Form.Item label="For success metrics, value expectations?">
            <MarkdownField
              ariaLabel="Success metrics"
              value={project.successMetrics || ""}
              onChange={(markdown) => onUpdate({ successMetrics: markdown })}
              placeholder="What are the specific metrics and expected values?"
              disabled={ro}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={
          <Space>
            <span>Impact Spotlight</span>
            {project.spotlightFeatured && <Tag color="magenta">Featured</Tag>}
          </Space>
        }
        extra={
          project.impactLastReviewedAt ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Last reviewed{" "}
              {dayjs(project.impactLastReviewedAt).format("MMM D, YYYY")}
              {project.impactReviewedBy ? ` · ${project.impactReviewedBy}` : ""}
            </Typography.Text>
          ) : null
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Surfaced on the Executive dashboard. Fill these in once a project has
          delivered outcomes you'd want OAIP leadership to see.
        </Typography.Paragraph>
        <Form layout="vertical" disabled={ro}>
          <Form.Item label="Featured on executive dashboard">
            <Switch
              aria-label="Feature this project in the executive impact spotlight"
              checked={!!project.spotlightFeatured}
              onChange={(checked) =>
                onUpdate({ spotlightFeatured: checked })
              }
            />
          </Form.Item>
          <Form.Item label="Headline">
            <Input
              aria-label="Impact headline"
              value={project.impactHeadline || ""}
              maxLength={140}
              showCount
              onChange={(event) =>
                onUpdate({ impactHeadline: event.target.value })
              }
              placeholder="e.g., DMV cut wait times in half with the new triage assistant"
            />
          </Form.Item>
          <Form.Item label="Story (markdown)">
            <MarkdownField
              ariaLabel="Impact story"
              value={project.impactStory || ""}
              onChange={(markdown) => onUpdate({ impactStory: markdown })}
              placeholder="One paragraph telling the outcome, the people served, and what it cost."
              disabled={ro}
              minHeight={120}
            />
          </Form.Item>
          <Form.Item label="Headline metrics (up to 3)">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {(project.impactMetrics || []).map((metric, index) => (
                <Row key={index} gutter={8} align="middle">
                  <Col span={8}>
                    <Input
                      aria-label={`Metric ${index + 1} value`}
                      value={metric.value || ""}
                      maxLength={64}
                      onChange={(event) => {
                        const next = [...(project.impactMetrics || [])];
                        next[index] = { ...next[index], value: event.target.value };
                        onUpdate({ impactMetrics: next });
                      }}
                      placeholder="12,000"
                    />
                  </Col>
                  <Col span={14}>
                    <Input
                      aria-label={`Metric ${index + 1} label`}
                      value={metric.label || ""}
                      maxLength={120}
                      onChange={(event) => {
                        const next = [...(project.impactMetrics || [])];
                        next[index] = { ...next[index], label: event.target.value };
                        onUpdate({ impactMetrics: next });
                      }}
                      placeholder="citizens served in 2026"
                    />
                  </Col>
                  <Col span={2}>
                    <Button
                      aria-label={`Remove metric ${index + 1}`}
                      icon={<DeleteOutlined />}
                      type="text"
                      disabled={ro}
                      onClick={() => {
                        const next = [...(project.impactMetrics || [])];
                        next.splice(index, 1);
                        onUpdate({ impactMetrics: next });
                      }}
                    />
                  </Col>
                </Row>
              ))}
              {(project.impactMetrics?.length || 0) < 3 && (
                <Button
                  icon={<PlusOutlined />}
                  disabled={ro}
                  onClick={() =>
                    onUpdate({
                      impactMetrics: [
                        ...(project.impactMetrics || []),
                        { value: "", label: "" },
                      ],
                    })
                  }
                >
                  Add metric
                </Button>
              )}
            </Space>
          </Form.Item>
          <Form.Item label="Image URL (optional)">
            <Input
              aria-label="Impact image URL"
              value={project.impactImageUrl || ""}
              onChange={(event) =>
                onUpdate({ impactImageUrl: event.target.value })
              }
              placeholder="https://… (uploaded to Firebase Storage or a public CDN)"
            />
          </Form.Item>
        </Form>
      </Card>

      <Card title="Data Information">
        <Form layout="vertical" disabled={ro}>
          <Form.Item label="Data Availability">
            <MarkdownField
              ariaLabel="Data availability"
              value={project.dataAvailability || ""}
              onChange={(markdown) => onUpdate({ dataAvailability: markdown })}
              placeholder="What data is available for this project?"
              disabled={ro}
            />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={project.usesLowRiskData || false}
              onChange={(event) =>
                onUpdate({ usesLowRiskData: event.target.checked })
              }
            >
              Uses Low Risk Data
            </Checkbox>
          </Form.Item>
          <Form.Item label="Data Sources/Names/Types">
            <MarkdownField
              ariaLabel="Data sources"
              value={project.dataSources || ""}
              onChange={(markdown) => onUpdate({ dataSources: markdown })}
              placeholder="List the data sources, names, and types..."
              disabled={ro}
            />
          </Form.Item>
          <Form.Item label="Amount of Historical Data">
            <Input
              value={project.historicalDataAmount || ""}
              onChange={(event) =>
                onUpdate({ historicalDataAmount: event.target.value })
              }
              placeholder="e.g., 5 years, 10,000 records, etc."
            />
          </Form.Item>
        </Form>
      </Card>

      <Card title="Technical & Vendor Information">
        <Form layout="vertical" disabled={ro}>
          <Form.Item label="Preferred Vendor/Technology">
            <MarkdownField
              ariaLabel="Preferred vendor or technology"
              value={project.preferredVendorTech || ""}
              onChange={(markdown) => onUpdate({ preferredVendorTech: markdown })}
              placeholder="Any preferred vendors or technologies?"
              disabled={ro}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card title="Ethics & Risk">
        <Form layout="vertical" disabled={ro}>
          <Form.Item label="Ethics/Decision Making Capabilities">
            <MarkdownField
              ariaLabel="Ethics and decision making capabilities"
              value={project.ethicsDecisionMaking || ""}
              onChange={(markdown) => onUpdate({ ethicsDecisionMaking: markdown })}
              placeholder="Ethical considerations and decision-making capabilities..."
              disabled={ro}
            />
          </Form.Item>
          <Form.Item label="Risks">
            <MarkdownField
              ariaLabel="Project risks"
              value={project.risks || ""}
              onChange={(markdown) => onUpdate({ risks: markdown })}
              placeholder="Identify potential risks..."
              disabled={ro}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card title="Points of Contact">
        <Form layout="vertical" disabled={ro}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Technical POC">
                <Input
                  value={project.technicalPOC || ""}
                  onChange={(event) =>
                    onUpdate({ technicalPOC: event.target.value })
                  }
                  placeholder="Technical point of contact"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Business POC">
                <Input
                  value={project.businessPOC || ""}
                  onChange={(event) =>
                    onUpdate({ businessPOC: event.target.value })
                  }
                  placeholder="Business point of contact"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card title="Additional Information">
        <Form layout="vertical" disabled={ro}>
          <Form.Item label="Level of Executive Support">
            <MarkdownField
              ariaLabel="Level of executive support"
              value={project.executiveSupport || ""}
              onChange={(markdown) => onUpdate({ executiveSupport: markdown })}
              placeholder="Describe the level of executive support..."
              disabled={ro}
            />
          </Form.Item>
          <Form.Item label="Other Notes">
            <MarkdownField
              ariaLabel="Other notes"
              value={project.otherNotes || ""}
              onChange={(markdown) => onUpdate({ otherNotes: markdown })}
              placeholder="Any other relevant notes..."
              disabled={ro}
              minHeight={120}
            />
          </Form.Item>
        </Form>
      </Card>
    </Space>
  );
}
